import { useStripe } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUserContext } from '../contexts/UserContext';
import {
  selectAddonsList,
  selectAddOnSuccessFlag,
  selectCartAddonsList,
  selectDefaultPaymentMethod,
  selectGetCardList,
  selectHubspotItem,
  selectUserActivePlanDetails,
  selectUserActivePlanSubscriptionId,
  selectUserPlanList,
  subscriptionActions,
} from '../redux/slicers/subscriptionSlicer';
import {
  RECURRING_INTERVAL,
  RECURRING_INTERVAL_INFO,
} from '../screens/subscriptionComponents/subscriptionHelper';
import { usePermissionsHandler, useStaticPermissions } from './usePermissions';
import {
  AddAddonCart,
  AddAddonToSubscription,
  AddAddonToSubscriptionV2,
  BuyOneTimeAddOnPlan,
  GetAllAddonsProductList,
  GetAllCartProductList,
  GetPurchasedOnAddonsList,
  removeAllItemsFromCart,
  setClientSubscriptionPlans,
  setClientSubscriptionPlansV2,
  UpdateAddonToSubscription,
  UpdateAddonToSubscriptionV2,
  UpgradeToNewPlanPatchV2,
  UpgradeToNewPlanV2,
} from '../api/subscription/subscription';
import { PERMISSION, PERMISSIONS } from '../constants/permissionConstants';
import { isAPIError, wait } from '../helpers/jsHelper';
import { errorToast, infoToast, warningToast } from '../helpers/toastHelper';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dispatcher, navigate } from '../helpers/projectHelper';

var isPaymentInitiated = false;
var isSuccessMessageShow = false;

export const handleAddonsPurchaseResponse = async ({
  stripe,
  response,
  backToReferrerUrl,
  savedDefaultPM,
}) => {
  if (!stripe || !response) {
    isAPIError(true);
    return;
  }

  if (!savedDefaultPM) {
    errorToast('Please select a payment method to continue.');
    return;
  }

  if (response?.data?.SCA && response?.data?.client_secret) {
    const paymentIntentClientSecret = response?.data?.client_secret;

    await stripe
      .confirmPayment({
        clientSecret: paymentIntentClientSecret,
        confirmParams: {
          payment_method: savedDefaultPM,
          return_url: window.location.href,
        },
        redirect: 'if_required',
      })
      .then(async ({ error, paymentIntent }) => {
        if (error) {
          if (error.param === 'mandate_data' && error.payment_intent.status === 'processing') {
            infoToast('Your payment is currently being processed.');
          } else if (error.payment_intent?.status === 'requires_payment_method') {
            errorToast(error.message || 'Please add payment method.');
            isAPIError(true);
          } else if (error.payment_intent?.status === 'canceled') {
            // dispatcher(setFailedClientSubscriptionPlansAndAddOns());
            // dispatcher(setClientSubscriptionPlans());
            errorToast('Your payment is cancelled.');
            isAPIError(true);
          } else {
            if (error.payment_intent?.status === 'processing') {
              infoToast('Your payment is currently under processing.');
            } else {
              errorToast(error.message || 'Something went wrong. Please try again later.');
              isAPIError(true);
            }
          }
          // setCheckout(false);
        }
        if (paymentIntent?.status === 'succeeded') {
          await wait(3000);
          // dispatch(setClientSubscriptionPlans());
          dispatcher(
            subscriptionActions.SetAddOnsSuccessFlag({
              flag: true,
              message: 'Addons purchased successfully.',
            })
          ); // We have successfully processed your addon purchase. Your addons will be updated shortly

          await wait(3000);
          if (backToReferrerUrl) {
            navigate(decodeURI(backToReferrerUrl), { replace: true });
          }
        } else if (paymentIntent?.status === 'processing') {
          await wait(3000);
          // dispatch(setClientSubscriptionPlans());
          dispatcher(
            subscriptionActions.SetAddOnsSuccessFlag({
              flag: true,
              message: 'Your payment is currently under processing.',
            })
          );
        }
      });
  } else {
    await wait(3000);
    dispatcher(
      subscriptionActions.SetAddOnsSuccessFlag({
        flag: true,
        message: 'Addons purchased successfully.',
      })
    ); // We have successfully processed your addon purchase. Your addons will be updated shortly

    await wait(3000);
    if (backToReferrerUrl) {
      navigate(decodeURI(backToReferrerUrl), { replace: true });
    }
  }
};

export default function useAddOnsInfo({ clearCheckout = null } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const stripe = useStripe();
  const defaultPaymentMethod = useSelector(selectDefaultPaymentMethod);
  const { handlePermissionEvent } = usePermissionsHandler();
  const { currentUser } = useUserContext();
  const currentOrg = currentUser?.organization;
  const { hasPermission } = useStaticPermissions();
  const [searchParams] = useSearchParams();
  const ignoreIsSendPrice = searchParams.get('ignoreIsSendPrice');

  const { flag: addOnSuccessFlag, message: addOnSuccessMessage } =
    useSelector(selectAddOnSuccessFlag);

  const hubspotItem = useSelector(selectHubspotItem);
  const [billingCycle, setBillingCycle] = useState(RECURRING_INTERVAL.MONTH);
  const [addonsLoading, setAddonsLoading] = useState(false);

  const [purchaseAddOns, setPurchaseAddOns] = useState([]);
  const [filterPurchaseValue, setFilterPurchaseValue] = useState([]);

  const queryParameters = new URLSearchParams(location.search);
  const backToReferrerUrl = queryParameters.get('referrer')?.replace(window.location.origin, '');

  const { results: addonsLists } = useSelector(selectAddonsList);
  const { results: cartAddonsList } = useSelector(selectCartAddonsList);
  const activeSub = useSelector(selectUserActivePlanSubscriptionId);
  const activePlan = useSelector(selectUserActivePlanDetails);
  const activePlanId = activePlan?.id;
  const { results: paymentCardList } = useSelector(selectGetCardList);

  const getUserPlans = useSelector(selectUserPlanList);
  const userPurchasedPlans = getUserPlans?.filter((a) => !a.is_cancelled);

  const [hubSpotItems, setHubSpotItems] = useState(null);

  const getHubspotItems = async (listData) => {
    const _product = listData?.find(
      (_item) => +_item?.plan_details?.plan_id === +hubspotItem?.product_id
    );
    if (_product)
      setHubSpotItems({
        mainID: +hubspotItem?.product_id,
        actualValue: _product?.plan_details,
        child: _product?.plan_details,
        count: hubspotItem?.quantity,
        parent: { id: hubspotItem?.subscription_id },
      });
    else {
      await removeAllItemsFromCart();
    }
  };

  const _setAddonsList = (results) => {
    const allPrices = addonsLists?.reduce((a, c) => {
      return [
        ...a,
        ...c?.prices
          ?.filter((_item) => !_item?.is_archived && !_item.is_disabled)
          ?.map((_item) => ({ ..._item, basemodeltype: c?.basemodeltype })),
      ];
    }, []);

    const getMainStripePriceId = (c, _item) => {
      return allPrices?.find(
        (__item) =>
          __item?.basemodeltype === c?.basemodeltype &&
          +_item?.recurring_interval === +__item?.recurring_interval &&
          +_item?.recurring_interval_count === +__item?.recurring_interval_count
      );
    };

    setPurchaseAddOns(
      results?.map((__item) => {
        const planDetails = __item?.plan_details?.reduce((a, c) => {
          let flag = false;
          const calcualateAddonAddons = a.map((_item) => {
            if (_item?.basemodeltype === c?.basemodeltype) {
              flag = true;
              const mainStripePriceId = getMainStripePriceId(c, __item)?.stripe_price_id;
              return {
                ..._item,
                product_quantity: _item.product_quantity + c.product_quantity,
                to_remove_product_quantity:
                  _item.to_remove_product_quantity + c.to_remove_product_quantity,
                price_stripe_price_id: mainStripePriceId || _item.price_stripe_price_id,
              };
            }
            return _item;
          });

          if (flag) return calcualateAddonAddons;
          else {
            const mainStripePriceId = getMainStripePriceId(c)?.stripe_price_id;
            const price_stripe_price_id = mainStripePriceId || c?.price_stripe_price_id;

            return [...a, { ...c, price_stripe_price_id }];
          }
        }, []);

        return { ...__item, plan_details: planDetails };
      }) || []
    );
  };

  const getAddonsSubscriptionList = async () => {
    if (currentOrg) {
      const userPlans = await dispatch(GetPurchasedOnAddonsList());
      if (userPlans && userPlans?.results && userPlans?.results?.length > 0) {
        const results = userPlans?.results;
        _setAddonsList(results);
      } else {
        setPurchaseAddOns([]);
      }
    }
  };

  const getAllAddonsList = () => {
    dispatch(GetAllAddonsProductList({ is_new_self_serve_plan: true })); // activeSub
    dispatch(GetAllCartProductList()); // activeSub
    getAddonsSubscriptionList();
  };

  useEffect(() => {
    if (purchaseAddOns && purchaseAddOns?.length) {
      let listData = [];
      purchaseAddOns?.map((item) => {
        item?.plan_details
          ?.filter((a) => !a?.is_upload_limit_plan)
          ?.map((child) => {
            let value = { ...item };
            delete value.plan_details;
            listData.push({
              ...value,
              plan_details: child,
              subscription_id: item?.id,
            });
          });
      });
      setFilterPurchaseValue(listData || []);
      if (hubspotItem) {
        getHubspotItems(listData);
      }
    } else {
      filterPurchaseValue?.length && setFilterPurchaseValue([]);
    }
  }, [purchaseAddOns]);

  useEffect(() => {
    dispatch(GetAllCartProductList());
    getAddonsSubscriptionList();
  }, []);

  const handleRemoveNullPriceRecord = () => {
    const results = addonsLists?.filter((a) => a?.prices?.length !== 0);
    return results || [];
  };

  const filterAddonsItemsList = (item) => {
    return (
      item?.basemodeltype?.toLowerCase() === 'ai video' ||
      item?.prices?.every((_item) => _item.is_archived || _item.is_disabled)
    );
  };
  let filterAddonsList = handleRemoveNullPriceRecord();

  // used to add cart qty into main add-on list
  const modifyAddonsDetails = (list, cartAddonsList) => {
    if (cartAddonsList?.length) {
      const dataList = list.map((item) => {
        const isSendPrice = ignoreIsSendPrice ? true : !item?.isSendPrice;
        let cartItem = cartAddonsList.find(({ subscription_price, price }) =>
          price
            ? subscription_price === item?.id &&
              price === item?.prices?.[0]?.id &&
              item?.isSendPrice
            : subscription_price === item?.id && isSendPrice
        );
        if (!cartItem) {
          cartItem = cartAddonsList.find(
            ({ subscription_price }) => subscription_price === item?.id
          );
          return { ...item, quantity: cartItem?.quantity || 0 };
        }
        return { ...item, quantity: cartItem?.quantity || 0 };
      });
      return dataList;
    } else {
      return list;
    }
  };

  const handleSeparateIntervals = (addonItems) => {
    if (!addonItems?.length) return [];
    const newEntries = [];
    const product = addonItems?.map((product) => {
      let prices = [...product.prices];
      const checkedPrices = {};
      for (let i = prices.length - 1; i >= 0; i--) {
        let price = prices[i];
        const key = `${price.recurring_interval}_${price.recurring_interval_count}`;
        if (checkedPrices[key]) {
          const newProduct = { ...product, prices: [price], isSendPrice: true };
          newEntries.push(newProduct);
          let duplicatePrice = [...product?.prices];
          duplicatePrice.splice(i, 1);
          let duplicateProduct = { ...product };
          duplicateProduct.prices = duplicatePrice;
          product = duplicateProduct;
        } else {
          checkedPrices[key] = true;
        }
      }
      return product;
    });
    return [...product, ...newEntries];
  };

  const handleMergeRecordsByRecurringInterval = (data) => {
    return data.reduce((acc, item) => {
      const existingRecord = acc.find((record) => record.id === item.id);

      if (existingRecord) {
        // Merge prices, ensuring no duplicates based on recurring_interval
        const existingPrices = existingRecord.prices;
        item.prices.forEach((price) => {
          if (!existingPrices.some((p) => p.recurring_interval === price.recurring_interval)) {
            existingPrices.push(price);
          }
        });
      } else {
        // Add new record
        acc.push({ ...item, prices: [...item.prices] });
      }

      return acc;
    }, []);
  };

  const handleRemoveDisabledIdsPrices = (data) => {
    let filterData = data
      .map((item) => {
        // Filter out prices with is_disabled or is_archived set to true
        const filteredPrices = item.prices.filter(
          (price) => !price.is_disabled && !price.is_archived
        );
        // Return the item with updated prices if prices exist
        return filteredPrices.length > 0 ? { ...item, prices: filteredPrices } : null;
      })
      .filter(Boolean); // Remove null values
    return handleMergeRecordsByRecurringInterval(filterData);
  };

  filterAddonsList = handleSeparateIntervals(filterAddonsList);
  filterAddonsList = handleRemoveDisabledIdsPrices(filterAddonsList);

  filterAddonsList = modifyAddonsDetails(filterAddonsList, cartAddonsList);

  const filterRecurringAddons = (data) => {
    return data?.filter((list) => !list?.is_one_time && !filterAddonsItemsList(list));
  };

  const filterOneTimeAddons = (data) => {
    return data?.filter(
      (list) => list?.is_one_time && list?.is_visible && !filterAddonsItemsList(list)
    );
  };

  const recurringAddonsList = filterRecurringAddons(filterAddonsList);
  const oneTimeAddonsList = filterOneTimeAddons(filterAddonsList);

  const handleAddAddonCart = async (value, isAdd) => {
    if (!hasPermission(PERMISSION.SUBSCRIPTION)) {
      warningToast(PERMISSIONS.SUBSCRIPTION.warningMessage);
      return;
    }

    let payload = {
      subscription_price: value?.id,
      quantity: 1,
      price: null,
    };
    if (value?.isSendPrice) {
      payload.price = value?.prices[0]?.id;
    } else {
      delete payload.price;
    }
    payload.quantity = isAdd
      ? (+value?.quantity || 0) + 1
      : +value?.quantity === 0
      ? +value?.quantity
      : +value?.quantity - 1;
    let indexVal = -1;
    if (value?.isSendPrice) {
      indexVal = cartAddonsList?.findIndex(
        ({ subscription_price, price }) =>
          +subscription_price === +value?.id && +price === +value?.prices[0]?.id
      );
    } else {
      indexVal = cartAddonsList?.findIndex(
        ({ subscription_price, price }) => +subscription_price === +value?.id && !price
      );
    }
    if (~indexVal) {
      let cloneVal = cartAddonsList?.slice() || [];
      cloneVal[indexVal] = { ...cloneVal[indexVal], quantity: payload.quantity };
      dispatch(subscriptionActions.setCartAddonsList({ results: cloneVal }));
    } else {
      let cloneData = cartAddonsList?.slice() || [];
      cloneData.push({
        id: cartAddonsList?.length + 1,
        subscription_price: value?.id,
        quantity: payload?.quantity,
        products_detials: value,
        price: payload?.price,
      });
      dispatch(subscriptionActions.setCartAddonsList({ results: cloneData }));
    }
    await AddAddonCart(payload);
  };

  const handleOneTimeAddonPurchase = async ({ isHubSpotItem, errorMessage, checkoutInfo }) => {
    const _checkoutInfo = checkoutInfo?.filter((a) => a.is_one_time && !a.is_archived);
    if (_checkoutInfo?.length === 0) return;

    const isRecurringItems = _checkoutInfo?.length - checkoutInfo.length ? true : false;
    const savedDefaultPM = defaultPaymentMethod?.stripe_payment_method_id;

    const priceId = [];
    if (!isHubSpotItem) {
      _checkoutInfo?.map((info) => {
        let oneTimeAddonsPlan =
          info?.prices?.find(({ recurring_interval, recurring_interval_count }) =>
            +billingCycle === 1
              ? recurring_interval === 1 && recurring_interval_count?.toString() === '1' // Monthly
              : +billingCycle === 2
              ? recurring_interval === 2 && recurring_interval_count?.toString() === '1' // Yearly
              : +billingCycle === 3
              ? recurring_interval === 1 && recurring_interval_count?.toString() === '3' // Quarterly
              : +billingCycle === 4
              ? recurring_interval === 1 && recurring_interval_count?.toString() === '6' // Semi annually
              : null
          ) || info?.prices?.[0];
        if (oneTimeAddonsPlan) {
          priceId.push({
            price: oneTimeAddonsPlan?.stripe_price_id,
            quantity: info?.quantity,
          });
        }
      });
    } else {
      priceId.push({
        price: isHubSpotItem?.actualValue?.price_stripe_price_id,
        quantity: isHubSpotItem?.count,
      });
    }

    if (!priceId?.length) {
      errorToast(errorMessage);
      setAddonsLoading(false);
      return;
    }

    const payload = {
      stripe_price_id: JSON.stringify(priceId),
      currency: 'usd',
      collection_method: 'charge_automatically',
      organization: currentOrg,
    };

    await BuyOneTimeAddOnPlan(payload)
      .then(async (response) => {
        if (response?.data?.SCA && response?.data?.client_secret) {
          const paymentIntentClientSecret = response?.data?.client_secret;

          await stripe
            .confirmPayment({
              clientSecret: paymentIntentClientSecret,
              confirmParams: {
                payment_method: savedDefaultPM,
                return_url: window.location.href,
              },
              redirect: 'if_required',
            })
            .then(async ({ error, paymentIntent }) => {
              if (error) {
                if (
                  error.param === 'mandate_data' &&
                  error.payment_intent.status === 'processing'
                ) {
                  infoToast('Your payment is currently being processed.');
                } else if (error.payment_intent?.status === 'requires_payment_method') {
                  isAPIError(true);
                  errorToast(error.message || 'Please add payment method.');
                } else if (error.payment_intent?.status === 'canceled') {
                  // dispatcher(setFailedClientSubscriptionPlansAndAddOns());
                  // dispatcher(setClientSubscriptionPlans());
                  isAPIError(true);
                  errorToast('Your payment is cancelled.');
                } else {
                  if (error.payment_intent?.status === 'processing') {
                    infoToast('Your payment is currently under processing.');
                  } else {
                    isAPIError(true);
                    errorToast(error.message || 'Something went wrong. Please try again later.');
                  }
                }
                // setCheckout(false);
              } else {
                if (paymentIntent?.status === 'succeeded') {
                  await wait(3000);
                  // dispatch(setClientSubscriptionPlans());
                  let message = 'Addons purchased successfully.'; // 'We have successfully processed your addon purchase. Your addons will be updated shortly.';
                  if (
                    response?.message
                      ?.toLowerCase()
                      ?.includes('added into your account, after payment is completed')
                  ) {
                    message = response?.message;
                  }
                  dispatch(
                    subscriptionActions.SetAddOnsSuccessFlag({
                      flag: !isRecurringItems,
                      message: message,
                    })
                  );

                  await wait(3000);
                  if (backToReferrerUrl && !isRecurringItems) {
                    navigate(decodeURI(backToReferrerUrl), { replace: true });
                  }
                } else if (paymentIntent?.status === 'processing') {
                  await wait(3000);
                  // dispatch(setClientSubscriptionPlans());
                  dispatch(
                    subscriptionActions.SetAddOnsSuccessFlag({
                      flag: !isRecurringItems,
                      message: 'Your payment is currently under processing.',
                    })
                  );
                }
              }
            });
        } else {
          await wait(3000);
          let message = 'Addons purchased successfully.'; // 'We have successfully processed your addon purchase. Your addons will be updated shortly.';
          if (
            response?.message
              ?.toLowerCase()
              ?.includes('added into your account, after payment is completed')
          ) {
            message = response?.message;
          }
          dispatch(
            subscriptionActions.SetAddOnsSuccessFlag({ flag: !isRecurringItems, message: message })
          );

          await wait(3000);
          if (backToReferrerUrl && !isRecurringItems) {
            navigate(decodeURI(backToReferrerUrl), { replace: true });
          }
        }
      })
      .catch((error) => {
        isAPIError(true);
        errorToast(error?.message || 'Something went wrong. Please try again!');
      })
      .finally(() => {
        // handleGetOneTimeAddonList();
        // dispatch(setClientSubscriptionPlans());
        // setAddonsLoading(false);
      });
  };

  const handleRecurringAddonPurchase = async ({ isHubSpotItem, errorMessage, checkoutInfo }) => {
    const _checkoutInfo = checkoutInfo?.filter((a) => !a.is_one_time && !a.is_archived);
    if (_checkoutInfo?.length === 0) return;

    let subscriptionAvailable = false;
    const priceId = [];
    if (!isHubSpotItem) {
      subscriptionAvailable = filterPurchaseValue?.find(
        ({ recurring_interval, recurring_interval_count }) =>
          +billingCycle === 1
            ? +recurring_interval === 1 && recurring_interval_count?.toString() === '1' // Monthly
            : +billingCycle === 2
            ? +recurring_interval === 2 && recurring_interval_count?.toString() === '1' // Yearly
            : +billingCycle === 3
            ? +recurring_interval === 1 && recurring_interval_count?.toString() === '3' // Quarterly
            : +billingCycle === 4
            ? +recurring_interval === 1 && recurring_interval_count?.toString() === '6' // Semi annually
            : null
      );

      _checkoutInfo?.map((info) => {
        let monthlyPlan = info?.prices.find(({ recurring_interval, recurring_interval_count }) =>
          +billingCycle === 1
            ? +recurring_interval === 1 && recurring_interval_count?.toString() === '1' // Monthly
            : +billingCycle === 2
            ? +recurring_interval === 2 && recurring_interval_count?.toString() === '1' // Yearly
            : +billingCycle === 3
            ? +recurring_interval === 1 && recurring_interval_count?.toString() === '3' // Quarterly
            : +billingCycle === 4
            ? +recurring_interval === 1 && recurring_interval_count?.toString() === '6' // Semi annually
            : null
        );
        if (monthlyPlan) {
          priceId.push({ price: monthlyPlan?.stripe_price_id, quantity: info?.quantity });
        }
      });
    } else {
      subscriptionAvailable = hubSpotItems?.parent?.id ? { id: hubSpotItems?.parent?.id } : null;
      priceId.push({
        price: isHubSpotItem?.actualValue?.price_stripe_price_id,
        quantity: isHubSpotItem?.count,
      });
    }

    if (!priceId?.length) {
      setAddonsLoading(false);
      errorToast(errorMessage);
      return;
    }
    const savedDefaultPM = defaultPaymentMethod?.stripe_payment_method_id;

    if (subscriptionAvailable) {
      let updatePayload = {
        stripe_price_id: JSON.stringify(priceId),
        paymentMethodId: defaultPaymentMethod?.stripe_payment_method_id,
        collection_method: 'charge_automatically',
        recurring_interval: isHubSpotItem
          ? isHubSpotItem?.parent?.recurring_interval
          : RECURRING_INTERVAL_INFO?.[billingCycle]?.interval,
        recurring_interval_count: isHubSpotItem
          ? isHubSpotItem?.parent?.recurring_interval_count
          : RECURRING_INTERVAL_INFO?.[billingCycle]?.count,
        add_subscription_items: true,
      };
      await UpdateAddonToSubscription(updatePayload, subscriptionAvailable?.id, true)
        .then(async (response) => {
          return await handleAddonsPurchaseResponse({
            stripe,
            response,
            backToReferrerUrl,
            savedDefaultPM,
          });
        })
        .catch((error) => {
          isAPIError(true);
          errorToast(error?.message || 'Something went wrong. Please try again!');
        })
        .finally(() => {
          // dispatch(setClientSubscriptionPlans());
          // setAddonsLoading(false);
        });
    } else {
      const priceId = [];
      if (!isHubSpotItem) {
        _checkoutInfo?.map((info) => {
          let monthlyPlan = info?.prices.find(({ recurring_interval, recurring_interval_count }) =>
            +billingCycle === 1
              ? +recurring_interval === 1 && recurring_interval_count?.toString() === '1' // Monthly
              : +billingCycle === 2
              ? +recurring_interval === 2 && recurring_interval_count?.toString() === '1' // Yearly
              : +billingCycle === 3
              ? +recurring_interval === 1 && recurring_interval_count?.toString() === '3' // Quarterly
              : +billingCycle === 4
              ? +recurring_interval === 1 && recurring_interval_count?.toString() === '6' // Semi annually
              : null
          );
          if (monthlyPlan) {
            priceId.push({
              price: monthlyPlan?.stripe_price_id,
              quantity: info?.quantity,
            });
          }
        });
      } else {
        priceId.push({
          price: isHubSpotItem?.actualValue?.price_stripe_price_id,
          quantity: isHubSpotItem?.count,
        });
      }
      if (!priceId?.length) {
        errorToast(errorMessage);
        setAddonsLoading(false);
        return;
      }
      let addPayload = {
        stripe_price_id: JSON.stringify(priceId),
        paymentMethodId: defaultPaymentMethod?.stripe_payment_method_id,
        collection_method: 'charge_automatically',
        is_add_on_subscription: true,
        recurring_interval: isHubSpotItem
          ? isHubSpotItem?.parent?.recurring_interval
          : RECURRING_INTERVAL_INFO?.[billingCycle]?.interval,
        recurring_interval_count: isHubSpotItem
          ? isHubSpotItem?.parent?.recurring_interval_count
          : RECURRING_INTERVAL_INFO?.[billingCycle]?.count,
      };
      const savedDefaultPM = defaultPaymentMethod?.stripe_payment_method_id;

      await AddAddonToSubscription(addPayload, activeSub)
        .then(async (response) => {
          return await handleAddonsPurchaseResponse({
            stripe,
            response,
            backToReferrerUrl,
            savedDefaultPM,
          });
        })
        .catch((error) => {
          isAPIError(true);
          errorToast(error?.message || 'Something went wrong. Please try again!');
        })
        .finally(() => {
          // dispatch(setClientSubscriptionPlans());
          // setAddonsLoading(false);
        });
    }
  };

  const handleRecurringAndOneTimeAddonPurchaseV2 = async ({
    isHubSpotItem,
    errorMessage,
    checkoutInfo,
  }) => {
    const _recurringAddonCheckoutInfo = checkoutInfo?.filter(
      (a) => !a.is_one_time && !a.is_archived
    );
    const _oneTimeAddonCheckoutInfo = checkoutInfo?.filter((a) => a.is_one_time && !a.is_archived);
    const recurringAddonPriceId = [];
    const oneTimeAddonPriceId = [];

    if (_recurringAddonCheckoutInfo?.length > 0) {
      if (!isHubSpotItem) {
        _recurringAddonCheckoutInfo?.map((info) => {
          let monthlyPlan = info?.prices.find(({ recurring_interval, recurring_interval_count }) =>
            +billingCycle === 1
              ? +recurring_interval === 1 && recurring_interval_count?.toString() === '1' // Monthly
              : +billingCycle === 2
              ? +recurring_interval === 2 && recurring_interval_count?.toString() === '1' // Yearly
              : +billingCycle === 3
              ? +recurring_interval === 1 && recurring_interval_count?.toString() === '3' // Quarterly
              : +billingCycle === 4
              ? +recurring_interval === 1 && recurring_interval_count?.toString() === '6' // Semi annually
              : null
          );
          if (monthlyPlan) {
            recurringAddonPriceId.push({
              price_id: monthlyPlan?.stripe_price_id,
              quantity: info?.quantity,
            });
          }
        });
      } else {
        // subscriptionAvailable = hubSpotItems?.parent?.id ? { id: hubSpotItems?.parent?.id } : null;
        recurringAddonPriceId.push({
          price_id: isHubSpotItem?.actualValue?.price_stripe_price_id,
          quantity: isHubSpotItem?.count,
        });
      }
    }

    if (_oneTimeAddonCheckoutInfo?.length > 0) {
      // if we don't have recurring addons selected then we are using the existing one time addons purchase api in which we are using 'price' key
      const priceIdKey = _recurringAddonCheckoutInfo.length > 0 ? 'price_id' : 'price';

      if (!isHubSpotItem) {
        _oneTimeAddonCheckoutInfo?.map((info) => {
          let oneTimeAddonsPlan =
            info?.prices?.find(({ recurring_interval, recurring_interval_count }) =>
              +billingCycle === 1
                ? recurring_interval === 1 && recurring_interval_count?.toString() === '1' // Monthly
                : +billingCycle === 2
                ? recurring_interval === 2 && recurring_interval_count?.toString() === '1' // Yearly
                : +billingCycle === 3
                ? recurring_interval === 1 && recurring_interval_count?.toString() === '3' // Quarterly
                : +billingCycle === 4
                ? recurring_interval === 1 && recurring_interval_count?.toString() === '6' // Semi annually
                : null
            ) || info?.prices?.[0];
          if (oneTimeAddonsPlan) {
            oneTimeAddonPriceId.push({
              [priceIdKey]: oneTimeAddonsPlan?.stripe_price_id,
              quantity: info?.quantity,
            });
          }
        });
      } else {
        oneTimeAddonPriceId.push({
          [priceIdKey]: isHubSpotItem?.actualValue?.price_stripe_price_id,
          quantity: isHubSpotItem?.count,
        });
      }
    }

    if (recurringAddonPriceId?.length === 0 && oneTimeAddonPriceId?.length === 0) {
      setAddonsLoading(false);
      errorToast(errorMessage);
      return;
    }

    const savedDefaultPM = defaultPaymentMethod?.stripe_payment_method_id;

    let payload = {
      paymentMethodId: savedDefaultPM,
      collection_method: 'charge_automatically',
      add_subscription_items: true,
      recurring_interval: isHubSpotItem
        ? isHubSpotItem?.parent?.recurring_interval
        : RECURRING_INTERVAL_INFO?.[billingCycle]?.interval,
      recurring_interval_count: isHubSpotItem
        ? isHubSpotItem?.parent?.recurring_interval_count
        : RECURRING_INTERVAL_INFO?.[billingCycle]?.count,
      // upload_limit_prices: [],
      recurring_prices: recurringAddonPriceId,
      one_time_items: oneTimeAddonPriceId,
    };

    const typeValue = RECURRING_INTERVAL_INFO?.[billingCycle]?.interval;

    let createNewSubscription = userPurchasedPlans?.find(
      ({ recurring_interval }) => +recurring_interval === typeValue
    );
    if (typeValue !== activePlan?.recurring_interval && !createNewSubscription) {
      createNewSubscription = true;
    }

    let api =
      createNewSubscription === true
        ? UpgradeToNewPlanV2
        : activePlanId
        ? UpdateAddonToSubscriptionV2
        : UpgradeToNewPlanV2;
    // let api = activePlanId ? UpdateAddonToSubscriptionV2 : BuyOneTimeAddOnPlan;

    if (recurringAddonPriceId.length === 0) {
      payload = {
        stripe_price_id: JSON.stringify(oneTimeAddonPriceId),
        currency: 'usd',
        collection_method: 'charge_automatically',
        organization: currentOrg,
      };
      api = BuyOneTimeAddOnPlan;
    }

    const id = createNewSubscription?.id || activePlanId;

    await api(payload, id, true)
      .then(async (response) => {
        return await handleAddonsPurchaseResponse({
          stripe,
          response,
          savedDefaultPM,
          backToReferrerUrl,
        });
      })
      .catch((error) => {
        isAPIError(true);
        errorToast(error?.message || 'Something went wrong. Please try again!');
      })
      .finally(() => {
        // dispatch(setClientSubscriptionPlans());
        // setAddonsLoading(false);
      });
  };

  const handlePurchase = async ({ isHubSpotItem, checkoutInfo, callback }) => {
    if (isPaymentInitiated) return;
    isPaymentInitiated = true;
    setAddonsLoading(true);

    const errorMessage = `Some of your selected items are not available in the ${
      billingCycle === 1
        ? 'monthly'
        : billingCycle === 2
        ? 'yearly'
        : billingCycle === 3
        ? 'quarterly'
        : 'semi-anuually'
    } payment option`;
    handlePermissionEvent(
      PERMISSION.SUBSCRIPTION,
      async (r) => {
        if (r === undefined) {
          if (paymentCardList?.length > 0) {
            await Promise.allSettled([
              await handleOneTimeAddonPurchase({ isHubSpotItem, errorMessage, checkoutInfo }),
              await handleRecurringAddonPurchase({ isHubSpotItem, errorMessage, checkoutInfo }),
            ]).finally(() => {
              if (isAPIError()) {
                isAPIError(false);
                setAddonsLoading(false);
              } else {
                dispatch(setClientSubscriptionPlans());
                getAddonsSubscriptionList();
                removeAllItemsFromCart();
                setAddonsLoading(false);
                callback?.();
              }
            });
          } else {
            dispatch(subscriptionActions.setShowCardForm(true));
            setAddonsLoading(false);
          }
          isPaymentInitiated = false;
        }
      },
      PERMISSIONS.SUBSCRIPTION.warningMessage
    )();
  };

  const handlePurchaseV2 = async ({ isHubSpotItem, checkoutInfo, callback }) => {
    if (isPaymentInitiated) return;
    isPaymentInitiated = true;
    setAddonsLoading(true);

    const errorMessage = `Some of your selected items are not available in the ${
      billingCycle === 1
        ? 'monthly'
        : billingCycle === 2
        ? 'yearly'
        : billingCycle === 3
        ? 'quarterly'
        : 'semi-anuually'
    } payment option`;
    handlePermissionEvent(
      PERMISSION.SUBSCRIPTION,
      async (r) => {
        if (r === undefined) {
          if (paymentCardList?.length > 0) {
            await Promise.allSettled([
              await handleRecurringAndOneTimeAddonPurchaseV2({
                isHubSpotItem,
                errorMessage,
                checkoutInfo,
              }),
            ]).finally(() => {
              if (isAPIError()) {
                isAPIError(false);
                setAddonsLoading(false);
              } else {
                dispatch(setClientSubscriptionPlansV2());
                getAddonsSubscriptionList();
                removeAllItemsFromCart();
                setAddonsLoading(false);
                callback?.();
              }
            });
          } else {
            dispatch(subscriptionActions.setShowCardForm(true));
            setAddonsLoading(false);
          }
          isPaymentInitiated = false;
        }
      },
      PERMISSIONS.SUBSCRIPTION.warningMessage
    )();
  };

  // removing cart items when the addons are successfully purchased
  const handleRemoveAddonCart = async () => {
    if (isSuccessMessageShow) return;
    isSuccessMessageShow = true;
    setTimeout(
      () => dispatch(subscriptionActions.SetAddOnsSuccessFlag({ flag: false, message: '' })),
      5000
    );
    const payload = {
      is_purchased: 'true',
    };
    await AddAddonCart(payload);
    getAllAddonsList();
    clearCheckout?.();
  };

  // removing cart items when we get the true success flag
  useEffect(() => {
    if (addOnSuccessFlag) handleRemoveAddonCart();

    return () => {
      isSuccessMessageShow = false;
    };
  }, [addOnSuccessFlag]);

  return {
    billingCycle,
    setBillingCycle,
    filterAddonsList,
    recurringAddonsList,
    oneTimeAddonsList,
    cartAddonsList,
    handleAddAddonCart,
    getAllAddonsList,
    loading: addonsLoading,
    handlePurchase,
    handlePurchaseV2,
    purchaseAddOns,
  };
}
